let maintenanceBlockListener = null;
let activeMaintenance = null;
let maintainerBypass = false;

const CLIENT_MAINTAINER_STORAGE_KEY = 'clientMaintainer';

export function setClientMaintainerFlag(isMaintainer) {
  try {
    if (isMaintainer) {
      localStorage.setItem(CLIENT_MAINTAINER_STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(CLIENT_MAINTAINER_STORAGE_KEY);
    }
  } catch (_e) {
    // ignore storage errors
  }
}

export function isClientMaintainer() {
  if (maintainerBypass) return true;
  try {
    return localStorage.getItem(CLIENT_MAINTAINER_STORAGE_KEY) === '1';
  } catch (_e) {
    return false;
  }
}

export function setMaintainerMaintenanceBypass(enabled) {
  maintainerBypass = Boolean(enabled);
}

const MAINTENANCE_EXEMPT_URL_PARTS = [
  '/api/maintenance/now',
  '/api/maintenance/scheduled',
  '/api/maintenance/notifications',
  '/api/auth/me',
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/verify-reset-code',
  '/api/health',
  '/health',
];

export function setMaintenanceBlockListener(listener) {
  maintenanceBlockListener = typeof listener === 'function' ? listener : null;
}

export function notifyMaintenanceBlock(maintenance) {
  if (maintenance && isClientMaintainer()) {
    return;
  }
  activeMaintenance = maintenance || null;
  if (maintenanceBlockListener) {
    maintenanceBlockListener(activeMaintenance);
  }
}

export function clearMaintenanceBlock() {
  notifyMaintenanceBlock(null);
}

export function isMaintenanceActive() {
  return activeMaintenance != null;
}

export function getActiveMaintenance() {
  return activeMaintenance;
}

export function isMaintenanceExemptUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return MAINTENANCE_EXEMPT_URL_PARTS.some((part) => url.includes(part));
}

export function shouldBlockApiRequest(url) {
  if (isClientMaintainer()) return false;
  return isMaintenanceActive() && !isMaintenanceExemptUrl(url);
}

export function createMaintenanceBlockedError() {
  const error = new Error('Service unavailable during scheduled maintenance.');
  error.code = 'MAINTENANCE_BLOCKED';
  error.isMaintenanceBlocked = true;
  return error;
}

export async function parseMaintenanceFromResponse(response) {
  if (!response || response.status !== 503) return null;
  try {
    const data = await response.clone().json();
    return data?.maintenance || null;
  } catch (_e) {
    return null;
  }
}

export function formatMaintenanceCountdown(totalMinutes) {
  if (totalMinutes == null || !Number.isFinite(totalMinutes)) return '';
  const minutes = Math.max(0, Math.round(totalMinutes));
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours <= 0) return `${mins} minute${mins === 1 ? '' : 's'}`;
  if (mins <= 0) return `${hours} hour${hours === 1 ? '' : 's'}`;
  return `${hours} hour${hours === 1 ? '' : 's'} ${mins} minute${mins === 1 ? '' : 's'}`;
}

export function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function computeMinutesUntilEnd(maintenance) {
  if (!maintenance?.end_utc) return maintenance?.minutes_until_end ?? null;
  const end = new Date(maintenance.end_utc);
  if (Number.isNaN(end.getTime())) return maintenance?.minutes_until_end ?? null;
  return Math.max(0, Math.round((end.getTime() - Date.now()) / 60000));
}

export function computeMinutesUntilStart(maintenance) {
  if (maintenance?.minutes_until_start != null) {
    return Math.max(0, Math.round(maintenance.minutes_until_start));
  }
  const start = new Date(maintenance?.start_utc);
  if (Number.isNaN(start.getTime())) return null;
  return Math.max(0, Math.round((start.getTime() - Date.now()) / 60000));
}
